import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  SmartConnectorContext,
  SmartConnectorErrors,
  defaultContext,
} from '../../context';
import {
  OnChangeVariableHandler,
  VariableSettings,
  VarsByHeaders,
} from './VariableSettings';
import SmartConnectorService from 'services/SmartConnectorService';
import {
  StyledAddVariableButton,
  StyledBottomButtons,
  StyledVariablesList,
} from './styles';
import type {
  ExecutionVariable,
  GenericOption,
  SmartConnector,
  SmartConnectorFlowLoad,
  SmartVariableDataType,
} from 'pages/SmartConnectors/types';
import { StyledLoader, StyledVariablesLoader } from '../styles';
import {
  getSmartConnectorVariables,
  getUniqueName,
  getVariableNameError,
  getVariableNamesByScope,
  isCSVLoadStep,
} from '../../../helpers';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { useToast, toastVariant } from '__components/ToastProvider';
import { VARIABLE_TYPES } from 'pages/SmartConnectors/constants';
import { useAsync } from 'react-use';
import { WithMobileWarning } from 'pages/SmartConnectors/components/WithMobileWarning';

type VariablesListProps = {
  scope: string;
  setScope: Dispatch<SetStateAction<string>>;
};

export const VariablesList = ({ scope, setScope }: VariablesListProps) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const {
    smartConnector,
    stepData,
    setStepData,
    metaData,
    handleUpdate,
    loading,
    setErrors,
  } = useContext(SmartConnectorContext);

  const { value: autodetectedVars = [], loading: loadingVars } = useAsync<
    () => Promise<ExecutionVariable[]>
  >(async () => {
    try {
      const { data } = await SmartConnectorService.generateExecutionVariables(
        smartConnector.id
      );
      const {
        data: { headers },
      } = await SmartConnectorService.getSmartConnector(smartConnector.id);

      smartConnectorHeadersRef.current = headers || {};

      await handleUpdate(
        Promise.resolve({ headers: smartConnectorHeadersRef.current })
      );
      return data;
    } catch (err) {
      showToast({
        message: t('Failed to get execution variables'),
        variant: toastVariant.FAILURE,
      });
      monitoringExceptionHelper(err, {
        extra: {
          smartConnectorId: smartConnector.id,
        },
      });
      return [];
    }
  }, [smartConnector.id]);

  const autodetectedVarsByHeaders = useMemo(() => {
    return autodetectedVars.reduce<VarsByHeaders>((acc, variable) => {
      if (variable.data_source) {
        acc[variable.data_source] = variable;
      }
      return acc;
    }, {});
  }, [autodetectedVars]);

  const hasVariablesRef = useRef<boolean>(
    !!(smartConnector.execution_variables || []).filter(
      ({ scope }) => smartConnector.headers?.[scope]
    ).length
  );

  const smartConnectorHeadersRef = useRef<SmartConnector['headers']>(
    smartConnector.headers || {}
  );
  smartConnectorHeadersRef.current = smartConnector.headers || {};

  useEffect(() => {
    const errors: SmartConnectorErrors['execution_variables'] = [];

    if (!hasVariablesRef.current && autodetectedVars.length) {
      hasVariablesRef.current = true;
      const namesByScope = getVariableNamesByScope({
        flow: smartConnector.flow,
        execution_variables: smartConnector.execution_variables || [],
        headers: smartConnectorHeadersRef.current,
      });

      setStepData((prev) => ({
        ...prev,
        execution_variables: (autodetectedVars || [])
          .sort(({ display_order: a }, { display_order: b }) => a - b)
          .map((variable) => {
            const uniqueName = getUniqueName(
              variable.name,
              namesByScope[variable.scope]
            );

            namesByScope[variable.scope].push(uniqueName);

            return {
              ...variable,
              name: uniqueName,
            };
          }),
      }));

      setErrors((prev) => ({
        ...prev,
        execution_variables: autodetectedVars.map(() => ({})),
      }));
    } else {
      setStepData((prev) => ({
        ...prev,
        execution_variables: (prev.execution_variables || []).map(
          (variable, i) => {
            const dataSourceExistsAndValid =
              variable.data_source &&
              (
                (smartConnectorHeadersRef.current || {})[variable.scope] || []
              ).some(({ name }) => name === variable.data_source);
            errors[i] = dataSourceExistsAndValid
              ? {}
              : { data_source: [t('Data Source Column Not Found')] };
            return {
              ...variable,
              data_source: dataSourceExistsAndValid
                ? variable.data_source
                : null,
            };
          }
        ),
      }));

      setErrors((prev) => ({
        ...prev,
        execution_variables: errors,
      }));
    }
  }, [
    autodetectedVars,
    setStepData,
    setErrors,
    t,
    smartConnector.flow,
    smartConnector.execution_variables,
  ]);

  const sourceLabelsBySourceName = useMemo(() => {
    return Object.values(smartConnector.headers || {}).reduce(
      (acc, h) => {
        h.forEach(({ name, index }) => {
          acc[name] = `${name} (${index})`;
        });
        return acc;
      },
      {} as Record<string, string>
    );
  }, [smartConnector.headers]);

  const dataSourceOptions = useMemo(() => {
    const mappedSources = new Set(
      (stepData.execution_variables || []).map(({ data_source }) => data_source)
    );
    const scopedHeaders = (smartConnector.headers || {})[scope];
    return (scopedHeaders || [])
      .reduce(
        (acc, { name, index }) => {
          if (mappedSources.has(name)) {
            acc[1].options.push({
              value: name,
              label: `${name} (${index})`,
            });
          } else {
            acc[0].options.push({
              value: name,
              label: `${name} (${index})`,
            });
          }
          return acc;
        },
        [
          { options: [], label: t('Unmapped') },
          { options: [], label: t('Mapped') },
        ] as { options: GenericOption[]; label: string }[]
      )
      .filter(({ options }) => options.length);
  }, [smartConnector.headers, stepData.execution_variables, t, scope]);

  const onChangeVariableSettings: OnChangeVariableHandler = ({
    id,
    prop,
    value,
  }) => {
    let result = value;
    setStepData((prev) => ({
      ...prev,
      execution_variables: (prev.execution_variables || []).map(
        (variable, i) => {
          if (variable.id === id) {
            // sets unique name within scope with incrementation
            if (prop === 'name') {
              const names = getSmartConnectorVariables({
                execution_variables: (prev.execution_variables || []).filter(
                  (variable) =>
                    variable.id !== id &&
                    variable.name &&
                    variable.scope === scope
                ),
                flow: {
                  ...smartConnector.flow,
                  loads: (smartConnector.flow.loads || []).reduce<
                    SmartConnectorFlowLoad[]
                  >((acc, load) => {
                    if (isCSVLoadStep(load) && load.scope === scope) {
                      acc.push(load);
                    }
                    if (!isCSVLoadStep(load) && load.return_scopes?.length) {
                      const returnScopes = load.return_scopes.filter(
                        (returnScope) => returnScope.name === scope
                      );
                      if (returnScopes.length) {
                        acc.push({ ...load, return_scopes: returnScopes });
                      }
                    }
                    return acc;
                  }, []),
                },
              }).map(({ name }) => name);
              const nextValue = getUniqueName(value as string, names);

              result = nextValue;

              const nameError = getVariableNameError(nextValue as string, t);

              if (nameError) {
                // clearError is called on change inside VariableSettings, so we set error here with setTimeout to avoid race condition
                setTimeout(
                  () =>
                    setErrors((prev) => {
                      return {
                        ...prev,
                        execution_variables: prev.execution_variables?.map(
                          (error, index) => {
                            if (index === i) {
                              return {
                                ...error,
                                name: [nameError],
                              };
                            }
                            return error;
                          }
                        ),
                      };
                    }),
                  0
                );
              }

              return {
                ...variable,
                name: nextValue,
              };
            }

            // sets default input/output format based on data type and resets is_array/delimiter
            if (prop === 'data_type') {
              const {
                meta: { default_output_format, default_input_format },
              } =
                metaData.execution_variables.variable_data_types.find(
                  (dataType) => dataType.value === value
                ) ||
                defaultContext.metaData.execution_variables
                  .variable_data_types[0];
              return {
                ...variable,
                data_type: value as SmartVariableDataType,
                input_format: default_input_format,
                output_format: default_output_format,
                is_array: false,
                array_delimiter: null,
              };
            }

            // sets default delimiter if is_array is checked
            if (prop === 'is_array' && value && !variable.is_array) {
              return {
                ...variable,
                is_array: true,
                array_delimiter:
                  metaData.execution_variables.default_array_delimiter,
              };
            }

            return {
              ...variable,
              [prop]: value,
            };
          }
          return variable;
        }
      ),
    }));

    return result;
  };

  const onDeleteVariable = (id: string, index: number) => {
    setStepData((prev) => ({
      ...prev,
      execution_variables: (prev.execution_variables || []).filter(
        (variable) => variable.id !== id
      ),
    }));
    setErrors((prev) => ({
      ...prev,
      execution_variables: (prev.execution_variables || []).filter(
        (_, i) => index !== i
      ),
    }));
  };

  const onAddVariable = () => {
    setStepData((prev) => ({
      ...prev,
      execution_variables: [
        ...(prev.execution_variables || []),
        {
          id: `new_${Date.now()}`,
          name: '',
          data_type: null,
          data_source: null,
          is_array: false,
          required: false,
          input_format: null,
          output_format: null,
          array_delimiter: null,
          display_order: 0,
          type: VARIABLE_TYPES.data_source,
          scope,
        },
      ],
    }));
    setErrors((prev) => ({
      ...prev,
      execution_variables: [...(prev.execution_variables || []), {}],
    }));
  };

  const [variableIndexToScroll, setVariableIndexToScroll] = useState<
    number | null
  >(null);

  const handleAutodetectUnmappedColumns = async () => {
    setStepData((prev) => {
      let countAdded = 0;

      const mappedColumnsScopedLookup = (prev.execution_variables || [])
        .filter(
          (variable) =>
            smartConnectorHeadersRef.current![variable.scope] &&
            variable.scope === scope
        )
        .reduce(
          (acc, { data_source }) => {
            if (data_source) {
              acc[data_source] = true;
            }
            return acc;
          },
          {} as Record<string, boolean>
        );

      const namesByScope = getVariableNamesByScope({
        flow: smartConnector.flow,
        execution_variables: prev.execution_variables || [],
        headers: smartConnectorHeadersRef.current,
      });

      const unmappedVars = autodetectedVars.reduce((acc, variable) => {
        if (
          variable.data_source &&
          variable.scope === scope &&
          !mappedColumnsScopedLookup[variable.data_source]
        ) {
          const uniqueName = getUniqueName(
            variable.name,
            namesByScope[variable.scope]
          );
          namesByScope[variable.scope].push(uniqueName);
          acc.push({
            ...variable,
            name: uniqueName,
          });
          countAdded++;
        }
        return acc;
      }, [] as ExecutionVariable[]);

      const next = {
        ...prev,
        execution_variables: (prev.execution_variables || []).concat(
          unmappedVars
        ),
      };

      if (countAdded > 0) {
        setVariableIndexToScroll(
          (prev.execution_variables || []).filter((v) => v.scope === scope)
            .length
        );
      }

      setErrors((prev) => ({
        ...prev,
        execution_variables: (prev.execution_variables || []).concat(
          unmappedVars.map(() => ({}))
        ),
      }));

      showToast({
        message: t('Added {{countAdded}} new variables', { countAdded }),
        variant: toastVariant.SUCCESS,
      });

      return next;
    });
  };

  return (
    <WithMobileWarning>
      <StyledLoader loading={loading}>
        <StyledVariablesLoader loading={loadingVars} />
        <StyledVariablesList>
          {(stepData.execution_variables || []).map((variable, i) => {
            return (
              <VariableSettings
                scope={scope}
                setScope={setScope}
                key={variable.id}
                index={i}
                variable={variable}
                dataSourceOptions={dataSourceOptions}
                onChange={onChangeVariableSettings}
                onDelete={onDeleteVariable}
                indexToScroll={variableIndexToScroll}
                setIndexToScroll={setVariableIndexToScroll}
                autodetectedVarsByHeaders={autodetectedVarsByHeaders}
                sourceLabelsBySourceName={sourceLabelsBySourceName}
                disabled={loadingVars}
              />
            );
          })}
        </StyledVariablesList>
        <StyledBottomButtons>
          <StyledAddVariableButton
            variant="text"
            onClick={onAddVariable}
            disabled={loadingVars}
          >
            {t('+ Add Variable')}
          </StyledAddVariableButton>
          <StyledAddVariableButton
            variant="text"
            color="blue"
            onClick={handleAutodetectUnmappedColumns}
            disabled={loadingVars}
          >
            {t('Autodetect Unmapped Columns')}
          </StyledAddVariableButton>
        </StyledBottomButtons>
      </StyledLoader>
    </WithMobileWarning>
  );
};
